import type { Annotation as PluginAnnotation } from 'minerva-plugin-api';

/**
 * Every annotation the API returns: the full {@link Annotation}, but also the
 * narrower shapes that carry the same five fields without the optional article
 * (target participants, chemical ids, the project disease and organism).
 */
type ApiAnnotation = Omit<PluginAnnotation, 'annotatorClassName' | 'article'> &
  Partial<Pick<PluginAnnotation, 'article'>>;

/**
 * The API used to identify the annotator by its fully qualified Java class name
 * (`lcsb.mapviewer.annotation.services.annotators.HgncAnnotator`), and sent an
 * empty string for an annotation added by hand. It now sends the annotator name
 * (`HGNC`), or `null` for a hand-added one. Plugins are still given
 * `annotatorClassName`, carrying the closest thing left - the name.
 */
export const toPluginAnnotation = <T extends ApiAnnotation>(
  annotation: T,
): T & Pick<PluginAnnotation, 'annotatorClassName'> => ({
  ...annotation,
  annotatorClassName: annotation.annotator ?? '',
});

export const toPluginAnnotations = <T extends ApiAnnotation>(
  annotations: T[],
): (T & Pick<PluginAnnotation, 'annotatorClassName'>)[] => annotations.map(toPluginAnnotation);

/** The project disease and organism are absent on projects that declare neither. */
export const toOptionalPluginAnnotation = <T extends ApiAnnotation>(
  annotation: T | null | undefined,
): (T & Pick<PluginAnnotation, 'annotatorClassName'>) | null | undefined =>
  annotation === null || annotation === undefined ? annotation : toPluginAnnotation(annotation);
